import { useQuery } from '@tanstack/react-query';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';

/**
 * Custom hook to get user types from the user_types table
 * This is the source of truth for user types
 * 
 * @returns Object with userTypes array and helper booleans
 */
export function useUserTypes() {
  const session = useSession();
  const supabase = useSupabaseClient();

  const { data: userTypes = [], isLoading } = useQuery(
    ['user-types', session?.user?.id],
    async () => {
      if (!session?.user?.id) return [];

      const { data, error } = await supabase
        .from('user_types')
        .select('type')
        .eq('user_id', session.user.id)
        .maybeSingle(); // Use maybeSingle instead of single to handle 406 errors gracefully

      if (error) {
        // If no row found or 406 Not Acceptable (RLS/format issue), default to parent
        if (error.code === 'PGRST116' || error.code === 'PGRST301' || error.message?.includes('406')) {
          // Fallback to metadata if table query fails
          const fallbackTypes = 
            session.user.raw_user_meta_data?.user_types || 
            session.user.user_metadata?.user_types || 
            ['parent'];
          return Array.isArray(fallbackTypes) ? fallbackTypes : [fallbackTypes];
        }
        console.error('Error fetching user types:', error);
        // Fallback to metadata if table query fails
        const fallbackTypes = 
          session.user.raw_user_meta_data?.user_types || 
          session.user.user_metadata?.user_types || 
          ['parent'];
        return Array.isArray(fallbackTypes) ? fallbackTypes : [fallbackTypes];
      }

      return data?.type || ['parent'];
    },
    {
      enabled: !!session?.user?.id,
      staleTime: 30000, // Cache for 30 seconds
      cacheTime: 300000, // Keep in cache for 5 minutes
      retry: 1,
    }
  );

  const isParent = userTypes.includes('parent');
  const isCampOwner = userTypes.includes('camp-owner');
  const isAdmin = userTypes.includes('admin');

  return {
    userTypes,
    isParent,
    isCampOwner,
    isAdmin,
    isLoading,
  };
}

